import React, { useCallback, useMemo, useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/outline';
import { TanTable, SELECT_COLUMN_ID } from '../../components/TanTable';
import type { AcctDrillRow } from '../../services/accountingDashboards.service';
import { canCreateBucketInvoices, canSelectInvoice, invoiceSelectionError } from '../../services/accountingInvoiceSelection';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import AdditionalCostItemModal from '../../components/pro/AdditionalCostItemModal';
import { buildAcctDrillBulkActions } from '../../components/proBulkActions';
import { useCanUseChangeAll } from '../pro/useCanUseChangeAll';
import { usePrefetchChangeAllFields } from '../../hooks/useChangeAllFields';
import InvoiceCreationDialog from './InvoiceCreationDialog';
import { DrillTarget } from './accountingDrill';
import { drillColumnsFor, drillVisibilityFor, VIEWED_PRO_COLUMNS } from './drillColumns';
import { useDrillData } from './useDrillData';
import CardResultPill from '../../components/CardResultPill';
import type { FilterCondition } from '../../components/FilterConditionRow';

interface AccountingDrillCardProps {
  target: DrillTarget;
  /** The page's shared refresh counter; endpoint-backed cards refetch on it. */
  refreshTick: number;
  /** This bucket's custom filter, owned by the page so it survives a card remount. */
  conditions: FilterCondition[];
  /** Open the filter dialog for this bucket. */
  onEditFilter: () => void;
  /** Clear it — the toolbar's one-click escape, same path as the dialog's Clear. */
  onClearFilter: () => void;
  /** Human-readable condition lines for the badge's hover. */
  filterSummary?: string;
}

/**
 * The body of the open drill: just the results table. The host (AccountingDashboards)
 * owns the title bar, the "not wired" pill and the close/back control, whether it renders
 * this as a page swap or inside a Drawer.
 * The drill response names the member type; its column set shapes the table. The three
 * uncached rows stay placeholders, so the table is empty and the host says so.
 */
/**
 * Pinned columns, in V1's reading order: the identity of the row on the left, the things
 * you act with on the right. Master PRO and PRO anchor the left edge so a wide bucket
 * still says which order each row is; the C/V link and the selection checkbox sit hard
 * right so they stay reachable without scrolling back.
 *
 * Ids absent from a column set are simply not pinned, so one list serves every bucket.
 */
const LOCKED_COLUMNS = [
  { id: 'masterpro', side: 'left' as const },
  { id: 'pronumber', side: 'left' as const },
  { id: 'template_pro', side: 'left' as const },
  { id: 'listinvoice', side: 'right' as const },
];
const LOCKED_WITH_SELECTION = [...LOCKED_COLUMNS, { id: SELECT_COLUMN_ID, side: 'right' as const }];
const invoiceRowId = (row: AcctDrillRow) => String(row.invoice_recno ?? `pro-${row.pronumber}`);

const BucketResults: React.FC<AccountingDrillCardProps> = ({
  target, refreshTick, conditions, onEditFilter, onClearFilter, filterSummary,
}) => {
  const [revision, setRevision] = useState(0);
  const [invoiceRecnos, setInvoiceRecnos] = useState<number[] | null>(null);
  const { rows, memberType, rowType, filterable, loading, refreshing, error, notWired } =
    useDrillData(target.descriptor, refreshTick + revision, conditions);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [drawerPro, setDrawerPro] = useState<number | string | null>(null);
  // One additional-cost record, opened straight from an Accounts Payable row.
  const [costItem, setCostItem] = useState<{ pronumber: number; recno: number } | null>(null);
  // Stable like setDrawerPro: the columns memo keys off handler identity, and a fresh
  // arrow each render would rebuild the column array and fight the user's layout.
  const openAddlCost = useCallback(
    (pronumber: number, recno: number) => setCostItem({ pronumber, recno }), [],
  );
  const drillHandlers = useMemo(
    () => ({ onOpenPro: setDrawerPro, onOpenAddlCost: openAddlCost }), [openAddlCost],
  );
  const [changeAllPros, setChangeAllPros] = useState<string[] | null>(null);
  const canUseChangeAll = useCanUseChangeAll();
  // Resolve the registry's field set now rather than when the first checkbox mounts, so
  // the drawer's checkboxes paint right on their FIRST render — see the hook.
  usePrefetchChangeAllFields(canUseChangeAll);
  // Columns follow the ROW type (how V1 draws the bucket), not the member type (what a
  // member is) — the two differ for the masterpro-grouped pro families. Rebuilt only when
  // that changes: TanTable initializes pinning and sizing from the column array, so a fresh
  // identity every render would fight the user's layout. `setDrawerPro` is a stable setter.
  const columns = useMemo(() => drillColumnsFor(rowType ?? 'pro', drillHandlers), [rowType, drillHandlers]);
  // V1 draws a different column set per bucket. Everything is selected and available; this
  // hides what this bucket's V1 layout leaves out. TanTable treats it as the default for a
  // fresh table AND as the fallback for columns a saved layout predates, so a user who has
  // already arranged this grid keeps their arrangement.
  const defaultColumnVisibility = useMemo(
    () => drillVisibilityFor(target.rowKey, rowType ?? 'pro', drillHandlers),
    [target.rowKey, rowType, drillHandlers],
  );
  const selectable = !notWired && canCreateBucketInvoices(target.rowKey, memberType);
  // What a row in THIS bucket stands for. The masterpro-grouped families draw one row
  // per masterpro over a representative pro, so a pro-keyed fan-out there would reach
  // that one order and silently leave its siblings alone — buildAcctDrillBulkActions
  // sends those buckets to Search Results instead. Templates get neither: Change All on
  // a template is the template-edit gate, a different permission and a different board.
  const rowsAre = rowType === 'masterpro' || rowType === 'masterpro_hold'
    ? 'masterpros' as const
    : 'orders' as const;
  const bulkActions = useMemo(
    () => (notWired || !rowType || rowType === 'template'
      ? undefined
      : buildAcctDrillBulkActions({
          canChangeAll: canUseChangeAll,
          rowsAre,
          // Change All and the single-pro drawer are mutually exclusive — close the
          // single-pro drawer first so the two never overlap.
          onChangeAll: (pros) => {
            if (!pros.length) return;
            setDrawerPro(null);
            setChangeAllPros(pros);
          },
        })),
    [notWired, rowType, canUseChangeAll, rowsAre],
  );
  // Use current row objects: polling may change the Bill To or invoice status without
  // changing a selected ID. Never submit the stale objects from onSelectionChange.
  const selected = useMemo(() => rows.filter((row) => selectedIds.has(invoiceRowId(row)) && canSelectInvoice(row)), [rows, selectedIds]);
  const onSelectionChange = useCallback((selection: AcctDrillRow[]) => {
    setSelectedIds(new Set(selection.map(invoiceRowId)));
    setActionError(null);
  }, []);
  const selectionError = selected.length ? invoiceSelectionError(selected) : null;
  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionResetKey((key) => key + 1);
    setActionError(null);
  };
  const createInvoices = () => {
    if (loading || refreshing || error || !selectable) return;
    try {
      const invalid = invoiceSelectionError(selected);
      if (invalid) throw new Error(invalid);
      setInvoiceRecnos(selected.map((row) => Number(row.invoice_recno)));
      setActionError(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not open invoice creation.');
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col">
      <TanTable<AcctDrillRow>
        // Column pinning is initialized on mount. Mount the resolved type with its
        // selection lock, rather than inheriting the empty loading table's layout.
        key={rowType ?? 'loading'}
        data={rows}
        // 'pro' until the first response names the type — an empty table either way.
        columns={columns}
        // The host's title bar already shows the full breadcrumb; TanTable's own title bar gets the leaf.
        cardTitle={target.leafLabel}
        loading={loading}
        errorMessage={error}
        settingsKey={`acct-card-${target.rowKey}`}
        // The custom filter, where the bucket has an order to filter by. Template
        // buckets do not (their members carry no pronumber), so the control is hidden
        // there rather than offered and silently ignored.
        {...(notWired || !filterable ? {} : {
          onEditFilters: onEditFilter,
          showEditFiltersButton: true,
          editFiltersButtonIcon: 'cog' as const,
          editFiltersButtonTooltip: 'Filter this bucket',
          editFiltersActive: conditions.length > 0,
          customFilterCount: conditions.length,
          onClearCustomFilters: onClearFilter,
          headerExtra: (
            <CardResultPill
              includeClosed={false}
              filterCount={conditions.length}
              filterSummary={filterSummary}
            />
          ),
        })}
        // A placeholder's table is empty and typeless, so it must not persist a layout;
        // wired cards persist theirs under the same established key.
        disableSettingsPersistence={notWired || rowType === null}
        fillContainer
        enableColumnFilters
        enableRowSelection={selectable}
        canSelectRow={canSelectInvoice}
        getRowId={selectable ? invoiceRowId : undefined}
        onSelectionChange={onSelectionChange}
        selectionResetKey={selectionResetKey}
        lockedColumns={selectable ? LOCKED_WITH_SELECTION : LOCKED_COLUMNS}
        // The PRO column gets the green "viewed" pill every other 360 table gives it. The
        // C/V link tracks itself in its own namespace (drillColumns) rather than joining
        // this one, because a masterpro and a pro number are the same digits.
        trackViewedColumnId={VIEWED_PRO_COLUMNS}
        defaultColumnVisibility={defaultColumnVisibility}
        bulkActions={bulkActions}
        footerLeftContent={selectable && selected.length > 0 ? (
          <>
            <span className="text-sm text-label">{selected.length} selected</span>
            <button
              type="button"
              onClick={createInvoices}
              disabled={loading || refreshing || !!error || !!selectionError}
              title="Review and create invoices"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PrinterIcon className="h-4 w-4" />
              Create Invoices
            </button>
            <button type="button" onClick={clearSelection} className="text-sm text-label underline">Clear selection</button>
            {(selectionError || actionError) && <span role="alert" className="text-sm text-red-600 dark:text-red-400">{selectionError || actionError}</span>}
          </>
        ) : undefined}
      />
      {/* Mounted, not conditionally rendered — same as the Invoice List and EDI screens,
          so the drawer keeps its width and Master Pro navigation across opens. */}
      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        // No refetch on close, unlike the Invoice List: these rows come from the
        // cache_acct_dashboard_* tables, which only change when the sync job rebuilds
        // them, so a reload would re-fetch a bucket of up to ~150k members and still
        // show the pre-edit values. The page's Refresh control is the way back.
        onClose={() => setDrawerPro(null)}
      />
      {/* Change All — the drawer again, anchored on the first visible pro, with the
          flagged fields fanning out to the rest on Save. Unlike the single-pro drawer
          above this one DOES refresh on close: the fan-out has just rewritten these
          orders, and the cache row the grid is drawing is now stale in a way the user
          caused and expects to see. The refetch is the drill request, not a cache
          rebuild, so it still shows the last sync's membership. */}
      <ProDetailSlideOver
        pronumber={changeAllPros && changeAllPros.length > 0 ? changeAllPros[0] : null}
        isOpen={!!changeAllPros}
        onClose={() => {
          setChangeAllPros(null);
          setRevision((value) => value + 1);
        }}
        changeAll={changeAllPros ? { targetPros: changeAllPros } : null}
      />
      {invoiceRecnos && <InvoiceCreationDialog invoiceRecnos={invoiceRecnos} onClose={() => setInvoiceRecnos(null)} onCreated={() => {
        clearSelection();
        setRevision((value) => value + 1);
      }} />}
      {/* One additional-cost record. Unlike the single-pro drawer above, this DOES
          refresh on save — the same call setRevision makes after Change All and after
          invoice creation, because the user just rewrote the row the grid is drawing. */}
      {costItem && (
        <AdditionalCostItemModal
          isOpen
          pronumber={costItem.pronumber}
          recno={costItem.recno}
          onClose={() => setCostItem(null)}
          onSaved={() => setRevision((value) => value + 1)}
        />
      )}
    </div>
  );
};

// Switching buckets resets both the request's rows and selection immediately, including
// while the new bucket is loading. Shared by the page and drawer hosts.
const AccountingDrillCard: React.FC<AccountingDrillCardProps> = (props) => (
  // ViewedProsProvider is what makes the green pills work at all — ProTableCell no-ops
  // without it, silently. Mounted outside the keyed BucketResults so switching buckets
  // and coming back does not forget what you had already opened.
  <ViewedProsProvider>
    <BucketResults key={props.target.rowKey} {...props} />
  </ViewedProsProvider>
);

export default AccountingDrillCard;
